import { useState, useCallback, useEffect, useMemo } from 'react'
import { Participant } from '../pages/meeting-view/types/video-types'
import { useZoomContext } from '../context/ZoomContext'
import { orderBy } from '@galiprandi/array-tools'

const MAX_PARTICIPANTS = 1000

const useParticipants = () => {
    const { zoomClient, hasRaisedHands, setHasRaisedHands } = useZoomContext()
    const [participants, setParticipants] = useState<Participant[]>([])
    const [collaborators, setCollaborators] = useState<Participant[]>([])
    const [totalUserCount, setTotalUserCount] = useState<number>(0)

    const isCollaborator = (participant: Participant) => participant.isHost || participant.isManager

    const updateCollaborators = useCallback((updatedParticipants: Participant[]) => {
        setCollaborators(updatedParticipants.filter(isCollaborator))
    }, [])

    const updateParticipants = useCallback(
        (updatedParticipants: Partial<Participant> | Partial<Participant>[]) => {
            setParticipants(prev => {
                const updated = Array.isArray(updatedParticipants) ? updatedParticipants : [updatedParticipants]
                const newParticipants = prev.map(p => {
                    const update = updated.find(u => u.userId === p.userId)
                    if (update) {
                        return { ...p, ...update }
                    }
                    return p
                })

                // Always update collaborators
                const updatedCollaborators = newParticipants.filter(isCollaborator)

                // For non-collaborators, only update if they're already in the list or if we haven't reached MAX_PARTICIPANTS
                const updatedNonCollaborators = newParticipants
                    .filter(p => !isCollaborator(p))
                    .slice(0, MAX_PARTICIPANTS - updatedCollaborators.length)

                const finalParticipants = [...updatedCollaborators, ...updatedNonCollaborators]
                updateCollaborators(finalParticipants)
                return finalParticipants
            })
        },
        [updateCollaborators]
    )

    const addParticipants = useCallback(
        (newParticipants: Participant | Participant[]) => {
            setParticipants(prev => {
                const toAdd = Array.isArray(newParticipants) ? newParticipants : [newParticipants]

                // Always add collaborators
                const newCollaborators = toAdd.filter(isCollaborator)

                // For non-collaborators, only add if we haven't reached MAX_PARTICIPANTS
                const availableSlots = MAX_PARTICIPANTS - prev.length
                const newNonCollaborators = toAdd.filter(p => !isCollaborator(p)).slice(0, Math.max(0, availableSlots))

                const updatedParticipants = [...prev, ...newCollaborators, ...newNonCollaborators]

                updateCollaborators(updatedParticipants)
                return updatedParticipants
            })
            setTotalUserCount(prevCount => prevCount + (Array.isArray(newParticipants) ? newParticipants.length : 1))
        },
        [updateCollaborators]
    )

    const removeParticipants = useCallback(
        (removedParticipants: Participant | Participant[]) => {
            setParticipants(prev => {
                const toRemove = Array.isArray(removedParticipants) ? removedParticipants : [removedParticipants]
                const updatedParticipants = prev.filter(p => !toRemove.some(removed => removed.userId === p.userId))
                updateCollaborators(updatedParticipants)
                return updatedParticipants
            })
            setTotalUserCount(
                prevCount => prevCount - (Array.isArray(removedParticipants) ? removedParticipants.length : 1)
            )
        },
        [updateCollaborators]
    )

    useEffect(() => {
        if (!zoomClient) return
        const initialParticipants = zoomClient.getAllUser()
        setParticipants(initialParticipants.slice(0, MAX_PARTICIPANTS))
        setTotalUserCount(initialParticipants.length)
        updateCollaborators(initialParticipants)

        zoomClient.on('user-added', addParticipants)
        zoomClient.on('user-removed', removeParticipants)
        zoomClient.on('user-updated', updateParticipants)

        return () => {
            zoomClient.off('user-added', addParticipants)
            zoomClient.off('user-removed', removeParticipants)
            zoomClient.off('user-updated', updateParticipants)
        }
    }, [zoomClient, addParticipants, removeParticipants, updateParticipants, updateCollaborators])

    const orderedParticipant = useMemo(() => {
        if (!participants.length) return []

        const orderList = orderBy(participants, ({ muted }) => !!muted).slice(0, MAX_PARTICIPANTS)
        return orderList
    }, [participants])

    const findByParticipants = (query?: string) => {
        if (!query) return orderedParticipant
        const normalizedQuery = query.toLowerCase().trim()
        return orderedParticipant.filter(({ displayName }) => displayName.toLowerCase().includes(normalizedQuery))
    }

    return {
        participants,
        orderedParticipant,
        setParticipants,
        collaborators,
        hasRaisedHands,
        totalUserCount,
        isCollaborator,
        setHasRaisedHands,
        findByParticipants,
    }
}

export default useParticipants

// export const fakeParticipants: Participant[] = Array.from({ length: 1000 }, (_, index) => ({
//     userId: index + 1,
//     avatar: index % 5 === 0 ? 'https://example.com/avatar.png' : '',
//     displayName: `User ${index + 1}`,
//     isHost: false, //index === 0,
//     audio: index % 3 === 0 ? 'computer' : '',
//     bVideoOn: index % 2 === 0,
//     userIdentity: `${Math.random().toString(36).substring(2, 22)}`,
//     userGuid: `${crypto.randomUUID()}`,
//     isPhoneUser: index % 7 === 0,
//     isInFailover: index % 11 === 0,
//     isManager: false, //index % 13 === 0,
//     isAllowIndividualRecording: index % 17 === 0,
//     isVideoConnect: index % 19 !== 0,
//     sharerOn: index % 23 === 0,
//     sharerPause: index % 29 === 0,
//     muted: Math.random() > 0.1,
// }))
